// lib
import { List } from 'immutable'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { fromEvent as ObservableFromEvent, merge as rxMerge, EMPTY as EMPTY_OBSERVABLE } from 'rxjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  takeUntil as rxTakeUntil,
  mergeMap as rxMergeMap,
  filter as rxFilter,
  mapTo as rxMapTo,
  map as rxMap
} from 'rxjs/operators'

// styles
import '@aytm/lib/rfd-ui/styles/advanced/range-slider.scss'

//AYTM-2888 implement labels view with DOMElementRect()
//import { DOMElementRect } from '@aytm/lib/common/func'

// <Component>
RangeSlider.propTypes = {
  fromValue: PropTypes.number,
  toValue: PropTypes.number,
  onUpdate: PropTypes.func,
  readOnly: PropTypes.bool,
  options: PropTypes.instanceOf(List).isRequired,
  showMarks: PropTypes.bool,
  // positions: PropTypes.array.isRequired,
  className: PropTypes.string,
  sliderWidth: PropTypes.number.isRequired, // pixels
  samePositionRange: PropTypes.bool
}
export default React.memo(
  RangeSlider,
  (prevProps, nextProps) =>
    prevProps.options == nextProps.options &&
    prevProps.readOnly == nextProps.readOnly &&
    prevProps.className == nextProps.className &&
    prevProps.samePositionRange == nextProps.samePositionRange
)
function RangeSlider(props, _context) {
  const { options, readOnly, className, showMarks, sliderWidth, toValue, fromValue, onUpdate, samePositionRange } =
    props

  const positions = useMemo(() => options.toArray().map(option => option.get('text')), [options])

  const sliderStepSize = useMemo(() => sliderWidth / (positions.length - 1), [sliderWidth, positions.length])
  const getSliderPositionPX = useCallback(
    positionID => (sliderWidth / (positions.length - 1)) * positionID,
    [sliderWidth, positions.length]
  )
  const refGetSliderPositionPX = useRef(getSliderPositionPX)
  useEffect(() => void (refGetSliderPositionPX.current = getSliderPositionPX), [getSliderPositionPX])

  // FROM slider
  const refSliderFrom = useRef(null)
  const [from, setFromPosition] = useState(getPositionID('from', fromValue, options))

  // TO slider
  const refSliderTo = useRef(null)
  const [to, setToPosition] = useState(getPositionID('to', toValue, options))

  const refRangePX = useRef({ from: getSliderPositionPX(from), to: getSliderPositionPX(to) })
  const refRange = useRef({ from, to })
  const fromPX = refRangePX.current.from
  const toPX = refRangePX.current.to
  const refSliderBgStyle = useRef(getSliderBgStyle(refRangePX.current))

  // onUpdate
  const onUpdateCallback = useCallback(
    (from, to) => {
      onUpdate(options.getIn([from, 'value']), options.getIn([to, 'value']))
    },
    [onUpdate, options]
  )
  const refOnUpdate = useRef(onUpdateCallback)
  useEffect(() => void (refOnUpdate.current = onUpdateCallback), [onUpdateCallback])

  // drag slider subscription
  const unsubscribeRef = useRef(null)
  useEffect(() => {
    if (unsubscribeRef.current && 'function' == typeof unsubscribeRef.current) {
      unsubscribeRef.current()
    }
    if (!readOnly) {
      unsubscribeRef.current = dragSlider({
        refGetSliderPositionPX,
        samePositionRange,
        refSliderBgStyle,
        setFromPosition,
        sliderStepSize,
        setToPosition,
        refSliderFrom,
        refSliderTo,
        refOnUpdate,
        refRangePX,
        refRange,
        positionsLength: positions.length,
        readOnly
      })
    }
  }, [positions.length, readOnly, samePositionRange, sliderStepSize])

  const sliderRef = useRef(null)
  const onSliderClick = useCallback(
    event => {
      const slider = sliderRef.current.getBoundingClientRect()
      const id = Math.max(
        0,
        Math.min(positions.length - 1, Math.round(((positions.length - 1) / slider.width) * (event.clientX - slider.x)))
      )
      if (Math.abs(id - from) >= Math.abs(id - to)) {
        refOnUpdate.current(from, id)
        refRange.current['to'] = id
        refRangePX.current['to'] = getSliderPositionPX(id)
        refSliderBgStyle.current = getSliderBgStyle(refRangePX.current)
        setToPosition(id)
      } else {
        refOnUpdate.current(id, to)
        refRange.current['from'] = id
        refRangePX.current['from'] = getSliderPositionPX(id)
        refSliderBgStyle.current = getSliderBgStyle(refRangePX.current)
        setFromPosition(id)
      }
    },
    [from, to, getSliderPositionPX, positions.length]
  )

  // * * * L O G * * *
  // console.log('bg:', refSliderBgStyle.current)
  // console.log(`range [${from}, ${to}]`, `[${fromPX}px, ${toPX}px]`)
  // console.log('positions:', positions)
  // console.log(`positions [${from}, ${to}]`, positions[from], positions[to])

  return (
    <div
      ref={sliderRef}
      className={classNames({
        ['rfd-ui-range-slider']: true,
        [className]: className,
        ['rfd-ui-range-slider-read-only']: readOnly
      })}
      onClick={readOnly ? null : onSliderClick}
      style={{ width: `${sliderWidth}px` }}
    >
      {showMarks && (
        <div className="rfd-ui-slider-marks">
          {positions.map((_, index) => (
            <span className="rfd-ui-slider-mark" style={{ left: `${getSliderPositionPX(index)}px` }} key={index} />
          ))}
        </div>
      )}
      <span className="rfd-ui-range-slider-bg" style={refSliderBgStyle.current} />
      <span className="rfd-ui-slider-handle rfd-ui-slider-from" ref={refSliderFrom} style={{ left: `${fromPX}px` }}>
        <div className="rfd-ui-slider-arrow">{renderArrow()}</div>
      </span>
      <span className="rfd-ui-slider-handle rfd-ui-slider-to" ref={refSliderTo} style={{ left: `${toPX}px` }}>
        <div className="rfd-ui-slider-arrow">{renderArrow()}</div>
      </span>
      {renderLabels(fromPX, positions[from], toPX, positions[to])}
    </div>
  )
}

function renderArrow() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28">
      <filter id="shadow">
        <feDropShadow dx="1" dy="0.7" stdDeviation="1" floodColor="#AAAAAA" />
      </filter>
      <g id="Layer_2" data-name="Layer 2">
        <g id="Layer_4" data-name="Layer 4">
          <path
            filter="url(#shadow)"
            className="rfd-ui-slider-arrow-border"
            d="M10,1.06a.81.81,0,0,1,.76.45L18.89,20a.59.59,0,0,1-.07.61.85.85,0,0,1-.7.33H1.88a.85.85,0,0,1-.7-.33A.59.59,0,0,1,1.11,20L9.24,1.51A.81.81,0,0,1,10,1.06M10,0A1.85,1.85,0,0,0,8.27,1.09L.15,19.57a1.64,1.64,0,0,0,.17,1.65A1.87,1.87,0,0,0,1.88,22H18.12a1.87,1.87,0,0,0,1.56-.78,1.64,1.64,0,0,0,.17-1.65L11.73,1.09A1.85,1.85,0,0,0,10,0Z"
          />
          <path
            className="rfd-ui-slider-arrow-bg"
            d="M1.88,21.47a1.34,1.34,0,0,1-1.13-.56,1.1,1.1,0,0,1-.12-1.13L8.75,1.3a1.4,1.4,0,0,1,2.5,0l8.12,18.48a1.1,1.1,0,0,1-.12,1.13,1.34,1.34,0,0,1-1.13.56Z"
          />
        </g>
      </g>
    </svg>
  )
}

function renderLabels(fromPX, fromLabel, toPX, toLabel) {
  const diff = fromPX - toPX
  if (30 > Math.abs(diff)) {
    const labelPosition = (0 >= diff ? fromPX : toPX) + Math.abs(diff / 2) - 23
    return (
      <span className="rfd-ui-slider-arrow-label" style={{ left: `${labelPosition}px` }}>
        {0 == diff ? fromLabel : 0 < diff ? `${toLabel} - ${fromLabel}` : `${fromLabel} - ${toLabel}`}
      </span>
    )
  }
  return (
    <>
      <span className="rfd-ui-slider-arrow-label" style={{ left: `${fromPX - 23}px` }}>
        {fromLabel}
      </span>
      <span className="rfd-ui-slider-arrow-label" style={{ left: `${toPX - 23}px` }}>
        {toLabel}
      </span>
    </>
  )
}
function dragSlider(payload = {}) {
  const {
    refGetSliderPositionPX,
    samePositionRange,
    refSliderBgStyle,
    setFromPosition,
    sliderStepSize,
    setToPosition,
    refSliderFrom,
    refSliderTo,
    refOnUpdate,
    refRangePX,
    refRange,
    positionsLength,
    readOnly
  } = payload
  const sliderFromElement = refSliderFrom.current
  const sliderToElement = refSliderTo.current
  const rangePx = refRangePX.current
  const range = refRange.current
  const mousemove = document ? ObservableFromEvent(document, 'mousemove') : EMPTY_OBSERVABLE
  const mouseup = document ? ObservableFromEvent(document, 'mouseup') : EMPTY_OBSERVABLE
  const sliderFromMouseDown = sliderFromElement
    ? ObservableFromEvent(sliderFromElement, 'mousedown').pipe(
        rxMap(function (md) {
          return { md, sliderID: 'from' }
        })
      )
    : EMPTY_OBSERVABLE
  const sliderToMouseDown = sliderToElement
    ? ObservableFromEvent(sliderToElement, 'mousedown').pipe(
        rxMap(function (md) {
          return { md, sliderID: 'to' }
        })
      )
    : EMPTY_OBSERVABLE
  const clicks = rxMerge(
    sliderFromMouseDown.pipe(rxMapTo('sliderFromStartDrag')),
    sliderToMouseDown.pipe(rxMapTo('sliderToStartDrag')),
    mouseup.pipe(rxMapTo('stopDrag'))
  )
  const mousedrag = rxMerge(sliderFromMouseDown, sliderToMouseDown).pipe(
    rxMergeMap(function ({ md, sliderID } = {}) {
      const startX = md.clientX
      const startPositionID = range[sliderID]
      // calculate position
      return mousemove.pipe(
        rxMap(function (mm) {
          mm.preventDefault()
          const positionId = startPositionID + Math.round((mm.clientX - startX) / sliderStepSize)
          return {
            sliderID,
            // positionID: 0 > positionId ? 0 : positionId > positions.length - 1 ? positions.length - 1 : positionId
            positionID: 0 > positionId ? 0 : positionId > positionsLength - 1 ? positionsLength - 1 : positionId
          }
        }),
        rxFilter(
          ({ positionID }) =>
            range[sliderID] != positionID &&
            (samePositionRange || (!samePositionRange && positionID != range['from' == sliderID ? 'to' : 'from']))
        ),
        rxTakeUntil(mouseup)
      )
    })
  )

  // update slider position
  const dragSubscription = mousedrag.subscribe(({ sliderID, positionID }) => {
    range[sliderID] = positionID
    rangePx[sliderID] = refGetSliderPositionPX.current(positionID)
    refSliderBgStyle.current = getSliderBgStyle(rangePx)
    return 'from' == sliderID ? setFromPosition(positionID) : setToPosition(positionID)
  })

  // update cursor style & call update callback
  const clicksSubscription = clicks.subscribe(event => {
    if ('sliderFromStartDrag' == event) {
      sliderFromElement.style.cursor = 'grabbing'
      sliderFromElement.classList.toggle('active')
    }
    if ('sliderToStartDrag' == event) {
      sliderToElement.style.cursor = 'grabbing'
      sliderToElement.classList.toggle('active')
    }
    const activeSlider =
      'grabbing' == sliderFromElement.style.cursor
        ? sliderFromElement
        : 'grabbing' == sliderToElement.style.cursor
        ? sliderToElement
        : null
    if (!readOnly && 'stopDrag' == event && activeSlider) {
      if ('function' == typeof refOnUpdate.current) {
        refOnUpdate.current(Math.min(range.from, range.to), Math.max(range.from, range.to))
      }
      activeSlider.classList.toggle('active')
      activeSlider.style.cursor = 'grab'
    }
  })

  return () => {
    clicksSubscription.unsubscribe()
    dragSubscription.unsubscribe()
  }
}

function getSliderBgStyle({ from, to } = {}) {
  return {
    left: `${Math.min(from, to)}px`,
    width: `${Math.abs(from - to)}px`
  }
}
function getPositionID(sliderID, value, options) {
  if (!value) {
    return 'from' == sliderID ? 0 : options.size - 1
  }
  const foundID = options.findIndex(option => value == option.get('value'))
  return -1 < foundID ? foundID : 0
  // return options.reduce(
  //   (prevID, _, currID) => (Math.abs(options[currID] - value) < Math.abs(positions[prevID] - value) ? currID : prevID),
  //   0
  // )
}
